// openapi-fetch client with automatic Bearer-auth attachment and
// single-flight refresh on 401.
//
// The access token lives in the Zustand auth store (in-memory only).
// The refresh cookie is HttpOnly + SameSite=Strict; the browser sends
// it automatically on /api/v1/auth/refresh. We intercept 401s, call
// refresh once, and retry the original request with the new token.
// Concurrent 401s coalesce into a single refresh.

import createClient from "openapi-fetch";

import { endSession } from "@/auth/session";
import { getAccessToken, useAuth } from "@/auth/stub";

import type { paths } from "./generated";

let refreshPromise: Promise<string | null> | null = null;

async function postRefresh(): Promise<Response> {
  return fetch("/api/v1/auth/refresh", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
}

async function doRefresh(): Promise<string | null> {
  try {
    let res = await postRefresh();
    if (res.status === 409) {
      // Another tab rotated the refresh cookie a moment ago; the browser
      // now holds the new one. Retry once after a short pause.
      await new Promise((resolve) => setTimeout(resolve, 250));
      res = await postRefresh();
    }
    if (!res.ok) return null;
    const body = (await res.json()) as { access_token: string };
    useAuth.getState().setAccessToken(body.access_token);
    return body.access_token;
  } catch {
    return null;
  }
}

/**
 * Single-flight access-token refresh. Every caller that needs a new
 * access token — the 401 retry below, ``apiFetch``, and session
 * restore at app start — must go through here: the server rotates
 * the refresh cookie on each use, so two concurrent refreshes race
 * and the loser presents an already-rotated cookie (replay
 * detection → session revoked → "random" logout).
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise !== null) return refreshPromise;
  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

// Build a fresh Request with an updated Authorization header.
//
// Why go through the Request constructor instead of `fetch(url,
// init)`: if we extract `request.body` (a ReadableStream) and pass
// it back through init, browsers reject the call with "The `duplex`
// member must be specified for a request with a streaming body".
// Wrapping via `new Request(src, { headers })` carries the body
// across as a Request body (not an init stream), which sidesteps
// the duplex requirement entirely. We clone first so a 401-retry
// can produce a second usable copy.
export function withAuth(request: Request, token: string | null): Request {
  const headers = new Headers(request.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return new Request(request.clone(), { headers, credentials: "include" });
}

export async function authFetch(request: Request): Promise<Response> {
  const first = await fetch(withAuth(request, getAccessToken()));
  if (first.status !== 401 || request.url.endsWith("/auth/refresh")) {
    return first;
  }

  const fresh = await refreshAccessToken();
  if (!fresh) {
    endSession();
    return first;
  }
  return fetch(withAuth(request, fresh));
}

/**
 * ``fetch`` for authenticated endpoints that aren't (conveniently)
 * reachable through the typed ``api`` client. Same contract as the
 * client: bearer token attached from the auth store, one
 * single-flight refresh + retry on 401.
 *
 * ``path`` is an app-relative URL (``/api/v1/...``); any dynamic
 * segment must already be ``encodeURIComponent``-escaped by the
 * caller. ``init.body`` must be re-sendable (string / FormData /
 * Blob), never a stream, because a 401 replays the request.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const send = (token: string | null): Promise<Response> => {
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(path, { ...init, headers, credentials: "include" });
  };

  const first = await send(getAccessToken());
  if (first.status !== 401) return first;

  const fresh = await refreshAccessToken();
  if (!fresh) {
    endSession();
    return first;
  }
  return send(fresh);
}

// baseUrl must be an absolute URL so openapi-fetch's `new URL(base + path)`
// resolves under undici/node environments (vitest, SSR). In the browser
// this is the same origin the Vite dev server / production host serves
// from, so the Vite API proxy keeps working.
const _baseUrl =
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "http://localhost";

export const api = createClient<paths>({
  baseUrl: _baseUrl,
  fetch: authFetch,
});
